/**
 * @file Album related fragments.
 */

import gql from 'graphql-tag';

import { PrintPackageDetails } from './print-packages';
import { AlbumCustomPrintFields } from './pro-custom-prints';
import { AlbumCustomPrintDetails } from './custom-prints';
import { RosterIdentityBaseDetails } from './roster-identity';
import { AutoContributorSettingsDetails } from './contributor-assignments';
import {
  AlbumPhotoFavoriteDetails,
  PhotoFavoriteDetails,
  PhotoForegroundDetails,
} from './photo';
import accountCardDetails from '../../services/cards/graphql/fragments/accountCardDetails';
import { VideoDetails } from './video';
import { VendorDetails } from './account';
import { HierarchyGradeDetails } from './hierarchy-grades';
import { IdentityYearbookPhotoDetails } from './yearbook-photos';

export const AlbumRootAlbumFolderBaseDetails = gql`
  fragment AlbumRootAlbumFolderBaseDetails on Album {
    id
    root_album_folder {
      id
      upload_mode
      has_album
      file_comment_privacy
      individual_albums
      picture_day_enabled
      has_password
      invite_only_access
      join_code
      is_demo
      org_pay_photo_pricing_enabled
      default_album_delivery_mode
      price {
        value
        currency
      }
      album_price_mode
      joins_enabled
      matching_disabled
      face_recognition_disabled
      album_type
      album_subtype
      has_album_photos
      all_photos_mode
      event_settings {
        bride_name
        groom_name
        couple_email_address
        relationship
        anniversary_type
        first_name
        last_name
        charity_name
        event_date
        date_of_birth
        date_of_passing
        created_at
        updated_at
      }
      giftable_event_addon_types(exclude_purchased_event_addon_types: true)
      green_screen_enabled
      tags {
        total
        records {
          id
          ... on IAlbumFolderTag {
            name
            type
          }
        }
      }
      pricing_categories {
        total
        records {
          id
          pricing_category {
            id
            name
          }
        }
      }
    }
  }
`;

export const PhotoUrlTypeFields = gql`
  fragment PhotoUrlTypeFields on PhotoURLType {
    url
    width
    height
    size_code
  }
`;

export const PhotoThumbnails = gql`
  fragment PhotoThumbnails on Photo {
    id
    thumbnail_urls {
      ...PhotoUrlTypeFields
    }
  }
  ${PhotoUrlTypeFields}
`;

export const SlideshowCurationPhotoDetails = gql`
  fragment SlideshowCurationPhotoDetails on Photo {
    id
    is_album_slideshow_photo(album_id: $id)
    ...PhotoThumbnails
  }
  ${PhotoThumbnails}
`;

export const ManualMatchingPhotoDetails = gql`
  fragment ManualMatchingPhotoDetails on Photo {
    id
    album_photo_status(album_id: $id)
    ...PhotoThumbnails
    ...PhotoForegroundDetails
    faces(
      slice: { limit: 999 }
      exclude_machine_labeled_pub_boxes: true
      exclude_human_labeled_pub_boxes: false
      owned_only_human_labeled_pub_boxes: true
    ) {
      records {
        id
        facebox
        has_human_votes
        faux_face_type
        is_creator
        matched_photos(slice: { limit: 1 }, album_ids: [$id]) {
          records {
            id
            identity {
              id
              name
              last_name
              current_face {
                id
              }
              current_matching_face {
                id
              }
              current_photo {
                ...PhotoThumbnails
              }
              current_matching_photo {
                ...PhotoThumbnails
              }
              account_identity_name {
                id
                name
                last_name
              }
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${PhotoForegroundDetails}
`;

export const AlbumPhotoFaceHighlights = gql`
  fragment AlbumPhotoFaceHighlights on AlbumPhoto {
    id
    photo {
      id
      ...PhotoThumbnails
      ...PhotoFavoriteDetails
      ...PhotoForegroundDetails
      share_blocked
      is_owner
      is_album_slideshow_photo(album_id: $id)
      album_oauth_link_photo_exists(album_id: $id)
    }
    face_highlights {
      records {
        id
        rating
        type
        face {
          id
          facebox
          matched_photos(slice: { limit: 1 }) {
            records {
              id
              identity {
                id
                name
                current_photo {
                  ...PhotoThumbnails
                }
              }
            }
          }
        }
        album {
          id
          root_album_folder {
            id
          }
        }
      }
    }
    ...AlbumPhotoFavoriteDetails
    album {
      id
      name
      hash_code
      ...AlbumRootAlbumFolderBaseDetails
    }
    album_highlight {
      id
    }
  }
  ${PhotoThumbnails}
  ${PhotoFavoriteDetails}
  ${PhotoForegroundDetails}
  ${AlbumPhotoFavoriteDetails}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumPrices = gql`
  fragment AlbumPrices on Album {
    is_demo
    default_price_per_photo
    search_subscription_credits_joins_enabled
    org_pay_photo_pricing_enabled
    price_whole_album {
      value
      currency
    }
    photo_pricing {
      id
      requires_package_purchase
      digital_downloads_price {
        value
        currency
      }
      account {
        ...VendorDetails
      }
      print_items(slice: { limit: 999 }) {
        total
        records {
          id
          price {
            value
            currency
          }
          type {
            id
            name
          }
        }
      }
    }
  }
  ${VendorDetails}
`;

export const AlbumPhotoCount = gql`
  fragment AlbumPhotoCount on Album {
    id
    photos(include_unprocessed: true, slice: { limit: 25, offset: 0 }) {
      total
    }
  }
`;

export const AlbumFaceHighlightedPhotos = gql`
  fragment AlbumFaceHighlightedPhotos on Album {
    id
    face_highlighted_photos {
      total
    }
  }
`;

export const AlbumPhotoGroups = gql`
  fragment AlbumPhotoGroups on Album {
    id
    album_photo_groups(slice: { limit: 999 }, sort: $sort) {
      total
      records {
        id
        name
        cover_photos {
          records {
            faces_box
            ...PhotoThumbnails
          }
        }
        photos(
          include_unprocessed: true
          slice: { limit: $limit, offset: $offset }
          album_photo_filter: PUBLISHED
        ) {
          total
        }
        last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
          records {
            id
            ...PhotoThumbnails
            created_at
          }
        }
        display_order
      }
    }
  }
`;

export const AlbumTotalFavoritedPhotos = gql`
  fragment AlbumTotalFavoritedPhotos on Album {
    id
    favorited_photos {
      total
    }
  }
`;

export const AlbumForReview = gql`
  fragment AlbumForReview on Album {
    id
    photos_for_review: photos(album_photo_filter: REVIEWING) {
      total
    }
    album_folder_hierarchy {
      records {
        id
      }
    }
  }
`;

export const AlbumPendingCustomPrintDetails = gql`
  fragment AlbumPendingCustomPrintDetails on Album {
    id
    pending_purchased_custom_prints: purchased_custom_prints(
      slice: { limit: 1 }
      order_statuses: [PENDING]
    ) {
      total
    }
  }
`;

/**
 * Used to show status items within the Tree View.
 */
export const AlbumForStatusItems = gql`
  fragment AlbumForStatusItems on Album {
    id
    photos_for_review: photos(album_photo_filter: REVIEWING) {
      total
    }
    pending_purchased_custom_prints: purchased_custom_prints(
      slice: { limit: 1 }
      order_statuses: [PENDING]
    ) {
      total
    }
  }
`;

export const WebGalleryAlbumFields = gql`
  fragment WebGalleryAlbumFields on Album {
    is_web_gallery
    is_private_web_gallery
    download_without_watermark
    download_without_purchase
    print_without_watermark
    social_share_enabled
    hash_code
    is_demo
    purchased_photo_downloads(slice: { limit: 9999 }) {
      records {
        photo {
          id
        }
      }
    }
    web_gallery_print_purchases_enabled
    web_gallery_price_per_photo {
      value
      currency
    }
  }
`;

export const AlbumBasicDetails = gql`
  fragment AlbumBasicDetails on Album {
    id
    name
    join_code
    display_name
    album_type
    album_subtype
    hash_code
    last_uploaded_at
    joins_enabled
    is_video_album
    is_wedding_day_album
    is_memory_book
    is_reference_photos_album
  }
`;

export const AlbumListingDetails = gql`
  fragment AlbumListingDetails on Album {
    id
    name
    display_name
    display_order
    join_code
    is_paid
    is_demo
    album_type
    album_subtype
    pricing_tier
    last_uploaded_at
    start_date
    end_date
    is_video_album
    is_wedding_day_album
    is_memory_book
    is_reference_photos_album
    status
    creator {
      id
    }
    cover_photo {
      id
      faces_box
      thumbnail_urls(ignore_watermark: true) {
        ...PhotoUrlTypeFields
      }
      ...PhotoForegroundDetails
    }
    album_folder {
      id
      name
      individual_albums
      privacy
      has_children
      individual_photos_upload_type
      team_photos_upload_type
      tags {
        records {
          ... on IRecord {
            id
          }
        }
      }
    }
    ...AlbumRootAlbumFolderBaseDetails
    videos {
      total
      records {
        id
        type
        thumbnails {
          url
          width
        }
      }
    }
    ...AlbumPhotoCount
    last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
      records {
        id
        ...PhotoThumbnails
      }
    }
    ...AlbumPrices
    members {
      total
    }
    album_folder_hierarchy {
      records {
        id
      }
    }
    auto_contributor_settings {
      upload_access
    }
    has_children: has_album_photo_groups
    purchased_event_addon_types
    slideshow {
      mode
      status
      default_greeting_video {
        id
        thumbnails {
          url
          width
        }
      }
      dismissed_default_greeting_video
      layouts(slice: { limit: 1 }) {
        total
        records {
          account_card {
            front_preview_file_url
          }
        }
      }
      videos {
        total
      }
      photos {
        total
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PhotoForegroundDetails}
  ${AlbumPrices}
  ${PhotoThumbnails}
  ${AlbumPhotoCount}
  ${AlbumRootAlbumFolderBaseDetails}
`;

export const AlbumLocationFields = gql`
  fragment AlbumLocationFields on Location {
    id
    name
  }
`;

export const AlbumDetails = gql`
  fragment AlbumDetails on Album {
    id
    status
    name
    display_name
    join_code
    album_type
    album_subtype
    is_paid
    is_demo
    is_free
    start_date
    end_date
    hash_code
    album_location {
      ...AlbumLocationFields
    }
    location
    album_location {
      id
      name
      freeform_address
    }
    global_invitation_url
    enable_external_upload
    external_uploads_require_review
    ...AlbumPhotoCount
    highlights {
      id
    }
    pricing_tier
    album_folder {
      id
      name
      individual_albums
      org_pay_photo_pricing_enabled
      is_web_gallery
      joins_enabled
      album_type
      has_children
      individual_photos_upload_type
      team_photos_upload_type
    }
    root_album_folder {
      id
      green_screen_enabled
      face_recognition_disabled
      individual_photos_upload_type
      team_photos_upload_type
      pricing_categories {
        total
        records {
          id
          pricing_category {
            id
            name
          }
        }
      }
    }
    members {
      total
    }
    sports_game_venue
    privacy
    display_order
    description
    auto_contributor_share_url
    auto_contributor_settings {
      ...AutoContributorSettingsDetails
    }
    consumer_photos_sort_by
    consumer_photos_sort_direction
    joins_enabled
    public_join_code_verification_enabled
    picture_day_enabled
    is_video_album
    is_wedding_day_album
    is_memory_book
    is_reference_photos_album
    green_screen_enabled
    has_group_photos
    shoot_type
    hold_matched_photo_deliveries
    photo_search_enabled
    photo_search_allowed_to_be_enabled
    slideshow {
      status
    }
    ...WebGalleryAlbumFields
  }
  ${WebGalleryAlbumFields}
  ${AlbumPhotoCount}
  ${AlbumLocationFields}
  ${AutoContributorSettingsDetails}
`;

export const AlbumDeletedPhotosDetails = gql`
  fragment AlbumDeletedPhotosDetails on Album {
    deleted_photos {
      total
    }
  }
`;

export const AlbumDetailsWithFolderSubscription = gql`
  fragment AlbumDetailsWithFolderSubscription on Album {
    ...AlbumBasicDetails
    ...AlbumPrices
    album_print_packages {
      total
    }
    custom_prints(custom_print_types: [CUSTOM_PRINT]) {
      records {
        id
        ...AlbumCustomPrintDetails
      }
    }
    is_free
    tag_joins_disabled
    checkout_description
    album_folder {
      id
      name
      album_type
      album_subtype
      price {
        currency
        value
      }
    }
    subscription_album_folder {
      id
      name
      album_type
      album_subtype
      price {
        currency
        value
      }
    }
  }
  ${AlbumBasicDetails}
  ${AlbumPrices}
  ${AlbumCustomPrintDetails}
`;

export const AlbumMembers = gql`
  fragment AlbumMembers on Album {
    id
    selected_identities(
      slice: { limit: 9999 }
      dnp_status_filter: $dnpStatusFilter
      without_invitees: true
    ) {
      records {
        id
        from_invite
        created_at
        identity {
          id
          name
          last_name
          relationship
          current_matching_photo {
            ...PhotoThumbnails
          }
          current_photo {
            ...PhotoThumbnails
          }
          account {
            id
            name
            phone_number
          }
          account_identity_name {
            id
            name
            last_name
          }
        }
        matched_photos {
          total
        }
        purchased_photos {
          total
        }
        cabin_name
        jersey_number
        school_class
        has_purchased_album_identity
        matched_photo_notifications(slice: { limit: 1 }) {
          total
          records {
            created_at
          }
        }
        face_highlighted_photos {
          total
        }
        album_membership {
          id
          is_deleted
          account {
            id
            name
            phone_number
            current_selfie {
              ...PhotoThumbnails
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
`;

// @TODO For this to properly work, we need unique_roster_identities to care for identities
export const AlbumMembersRosterIdentities = gql`
  fragment AlbumMembersRosterIdentities on Album {
    id
    selected_identities(
      slice: { limit: $limit, offset: $offset }
      dnp_status_filter: $dnpStatusFilter
      without_invitees: true
      search_query: $searchQuery
      unique_roster_identities: $uniqueRosterIdentities
      sort_by: $sortBy
      claimed_status_filter: $claimedStatusFilter
      has_transactions: $hasTransactions
      has_matched_photos: $hasMatchedPhotos
      has_active_reference_faces_or_source_has: $hasActiveReferenceFaces
      grade: $grade
    ) {
      total
      records {
        id
        from_invite
        created_at
        identity {
          id
          name
          last_name
          relationship
          current_matching_photo {
            id
            is_owner
            ...PhotoThumbnails
          }
          current_photo {
            id
            is_owner
            ...PhotoThumbnails
          }
          ...IdentityYearbookPhotoDetails
          account {
            id
            name
            phone_number
            email_address
            email_verified
            type
            broadcast_recipients_count(hierarchy_item_id: $id)
            last_broadcast_recipient(hierarchy_item_id: $id) {
              id
              created_at
            }
          }
          account_identity_name {
            id
            name
            last_name
          }
          roster_identity {
            id
            ...RosterIdentityBaseDetails
            purchased_custom_prints {
              total
            }
          }
          has_outdated_selfie
          consumer_maybe_votes(hierarchy_item_id: $id) {
            total
          }
        }
        matched_photos_extended {
          total
        }
        plic_uploaded_photos: matched_photos_extended(plic_uploaded_only: true)
          @include(if: $includePlicUploadedPhotos) {
          total
        }
        purchased_photos {
          total
        }
        cabin_name
        jersey_number
        school_class
        has_purchased_album_identity
        matched_photo_notifications(slice: { limit: 1 }) {
          total
          records {
            id
            created_at
          }
        }
        face_highlighted_photos {
          total
        }
        album_membership {
          id
          is_deleted
          account {
            id
            name
            phone_number
            current_selfie {
              ...PhotoThumbnails
            }
          }
          transaction_items(
            exclude_prepaid_configurations: $excludePrepaidConfigurations
          ) {
            total
          }
        }
        claimed
        roster_identity {
          id
          email_addresses {
            records {
              id
              email_address
            }
          }
          selected_identities(album_id: $id) {
            total
            records {
              id
              identity {
                id
                name
                last_name
                account {
                  id
                  phone_number
                  email_address
                  email_verified
                  type
                  broadcast_recipients_count(hierarchy_item_id: $id)
                  last_broadcast_recipient(hierarchy_item_id: $id) {
                    id
                    created_at
                  }
                }
              }
              album_membership {
                id
                transaction_items {
                  total
                }
              }
              has_purchased_album_identity
              matched_photo_notifications(slice: { limit: 1 }) {
                total
                records {
                  id
                  created_at
                }
              }
              created_at
            }
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${RosterIdentityBaseDetails}
  ${IdentityYearbookPhotoDetails}
`;

export const AlbumWatermarkConfig = gql`
  fragment AlbumWatermarkConfig on Album {
    watermark_config {
      id
      placement
      logo_image {
        ...PhotoThumbnails
      }
      asset_image {
        id
        url
      }
      transparency
      size_ratio
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumDetailsForAssign = gql`
  fragment AlbumDetailsForAssign on Album {
    name
    id
    start_date
    end_date
    is_video_album
    is_wedding_day_album
    is_memory_book
    is_reference_photos_album
    org_pay_photo_pricing_enabled
    ...AlbumDeletedPhotosDetails
    ...AlbumPhotoCount
    photos_ungrouped: photos(
      include_unprocessed: true
      ungrouped_only: true
      slice: { limit: 25, offset: 0 }
      album_photo_filter: PUBLISHED
    ) {
      total
      records {
        id
      }
    }
    last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
      records {
        id
        ...PhotoThumbnails
      }
    }
    album_photo_groups(slice: { limit: 999 }) {
      total
      records {
        id
        name
        start_date
        end_date
        photos(
          include_unprocessed: true
          slice: { limit: 25, offset: 0 }
          album_photo_filter: PUBLISHED
        ) {
          total
          records {
            id
          }
        }
        last_uploaded: photos(include_unprocessed: true, slice: { limit: 1 }) {
          records {
            id
            ...PhotoThumbnails
          }
        }
      }
    }
  }
  ${PhotoThumbnails}
  ${AlbumDeletedPhotosDetails}
  ${AlbumPhotoCount}
`;

export const AlbumPrintPackagesPhotoPricingIds = gql`
  fragment AlbumPrintPackagesPhotoPricingIds on Album {
    id
    album_print_packages(slice: { limit: 9999 }) {
      total
      records {
        id
        print_package {
          id
        }
      }
    }
  }
`;

export const AlbumPrintPackageRecords = gql`
  fragment AlbumPrintPackageRecords on Album {
    id
    album_print_packages(slice: { limit: 9999 }) {
      total
      records {
        id
        print_package {
          ...PrintPackageDetails
        }
        album_folder_tags(slice: { limit: 9999 }) {
          records {
            ... on IRecord {
              id
            }
          }
        }
      }
    }
  }
  ${PrintPackageDetails}
`;

export const AlbumCustomPrintRecordsForPpdPurchase = gql`
  fragment AlbumCustomPrintRecordsForPpdPurchase on Album {
    id
    custom_prints(
      slice: { limit: 9999 }
      custom_print_types: [CUSTOM_PRINT, COLLAGE]
    ) {
      total
      records {
        ...AlbumCustomPrintDetails
      }
    }
  }
  ${AlbumCustomPrintDetails}
`;

export const AlbumCustomPrintItems = gql`
  fragment AlbumCustomPrintItems on Album {
    id
    custom_prints(slice: { limit: 9999 }) {
      total
      records {
        id
        ...AlbumCustomPrintFields
      }
    }
  }
  ${AlbumCustomPrintFields}
`;

export const AlbumHierarchyBreadcrumb = gql`
  query albumHierarchyBreadcrumb(
    $id: RecordID!
    $for_web_gallery: Boolean = false
    $use_all_photos_mode: Boolean = false
  ) {
    album(id: $id) {
      id
      name
      picture_day_enabled
      is_video_album
      is_wedding_day_album
      is_memory_book
      is_reference_photos_album
      album_type
      has_all_photos_access(
        for_web_gallery: $for_web_gallery
        use_all_photos_mode: $use_all_photos_mode
      )
      account_hierarchy_settings {
        id
        notifications_enabled
      }
      creator {
        id
      }
      album_folder {
        id
        individual_albums
      }
      root_album_folder {
        id
        individual_albums
        matching_disabled
      }
      album_folder_hierarchy {
        records {
          id
          name
          contributor_access {
            upload_access
            view_access
            album_create_access
            invite_enabled
          }
        }
      }
      photos(include_unprocessed: true) {
        total
      }
      videos(processed_only: false) {
        total
      }
      slideshow {
        status
      }
      joins_enabled
      matching_disabled
      purchased_event_addon_types
      account_favorite_photos {
        total
      }
    }
  }
`;

export const AlbumSlideshowDetails = gql`
  fragment AlbumSlideshowDetails on AlbumSlideshow {
    mode
    status
    slide_duration_ms
    slide_background_color
    slide_background_asset_image {
      id
      url
    }
    intro_asset_image {
      id
      url
    }
    qr_code {
      is_enabled
      display_callout
    }
    ticker {
      message
      color
      background_color
    }
    duration
    embed_code
    intro_slide_text
    intro_slide_text_color
    intro_slide_background_color
    intro_slide_label_enabled
  }
`;

export const AlbumSlideshowPhotosDetails = gql`
  fragment AlbumSlideshowPhotosDetails on AlbumSlideshow {
    photos(slice: { limit: $limit, offset: $offset }) {
      total
      records {
        photo {
          ...PhotoThumbnails
        }
        display_order
      }
    }
    unprocessed_photos: photos(unprocessed_only: true) {
      total
    }
  }
  ${PhotoThumbnails}
`;

export const AlbumSlideshowOwnerPhotosDetails = gql`
  fragment AlbumSlideshowOwnerPhotosDetails on AlbumSlideshow {
    excluded_photos(slice: { limit: 999 }) {
      total
      records {
        photo {
          id
        }
      }
    }
    selected_photos(slice: { limit: 999 }) {
      total
      records {
        photo {
          id
        }
      }
    }
  }
`;

export const AlbumSlideshowLayoutsDetails = gql`
  fragment AlbumSlideshowLayoutsDetails on AlbumSlideshow {
    intro_layout {
      account_card {
        ...accountCardDetails
        front_preview_file_url
      }
      display_order
    }
    layouts(slice: { limit: $limit, offset: $offset }) {
      total
      records {
        account_card {
          ...accountCardDetails
          front_preview_file_url
        }
        display_order
      }
    }
    unprocessed_photos: photos(unprocessed_only: true) {
      total
    }
  }
  ${accountCardDetails}
`;

export const AlbumSlideshowVideoDetails = gql`
  fragment AlbumSlideshowVideoDetails on AlbumSlideshow {
    mode
    status
    videos_layout
    default_greeting_video {
      ...VideoDetails
    }
    excluded_videos(slice: { limit: 999 }) {
      total
      records {
        video {
          id
        }
      }
    }
    videos(slice: { limit: $limit, offset: $offset }) {
      total
      records {
        display_order
        video {
          id
          ...VideoDetails
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumPurchasedEventAddonDetails = gql`
  fragment AlbumPurchasedEventAddonDetails on Album {
    purchased_event_addon_types
    purchased_event_addons {
      total
      records {
        id
        event_addon_type
      }
    }
  }
`;

export const AlbumHierarchyGradeDetails = gql`
  fragment AlbumHierarchyGradeDetails on Album {
    grades(slice: { limit: 99 }) {
      total
      records {
        ...HierarchyGradeDetails
      }
    }
  }
  ${HierarchyGradeDetails}
`;

export const AlbumForegroundCountDetails = gql`
  fragment AlbumForegroundCountDetails on Album {
    photos_pending_foregrounds: photos(
      include_unprocessed: true
      photo_foreground_status: PENDING
    ) {
      total
    }
    photos_completed_foregrounds: photos(
      include_unprocessed: true
      photo_foreground_status: COMPLETED
    ) {
      total
    }
  }
`;

export const AlbumLogoImageConfig = gql`
  fragment AlbumLogoImageConfig on Album {
    logo_image {
      id
      url
    }
  }
`;
